import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const HeroSlider = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      image: 'https://images.unsplash.com/photo-1758691462666-6470b740f544?w=1920&q=80',
      title: 'Compassionate Care for Every Generation',
      subtitle: '25+ years of free healthcare for KGF families',
      cta: 'Book Free Appointment'
    },
    {
      image: 'https://images.unsplash.com/photo-1731514721772-329626f84c8b?w=1920&q=80',
      title: 'Modern Diagnostics at No Cost',
      subtitle: 'MRI, pathology, and specialist consultations under one roof',
      cta: 'Our Services'
    },
    {
      image: 'https://images.unsplash.com/photo-1576765607924-3f7b8410a787?w=1920&q=80',
      title: 'Serving the Community Daily',
      subtitle: 'Open from 5:00 AM - hundreds helped every day',
      cta: 'Contact Us'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  return (
    <div className="relative w-full h-[500px] md:h-[600px] overflow-hidden" data-testid="hero-slider">
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
          data-testid={`hero-slide-${index}`}
        >
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${slide.image})` }}
            loading={index === 0 ? 'eager' : 'lazy'}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
          </div>

          {/* Content */}
          <div className="relative z-20 h-full flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-2xl">
                <h1
                  className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight"
                  data-testid={`slide-title-${index}`}
                >
                  {slide.title}
                </h1>
                <p className="text-lg sm:text-xl text-gray-200 mb-8" data-testid={`slide-subtitle-${index}`}>
                  {slide.subtitle}
                </p>
                <Button
                  size="lg"
                  className="bg-cyan-600 hover:bg-cyan-700 text-lg px-8 py-6"
                  data-testid={`slide-cta-${index}`}
                  onClick={() => {
                    const route = index === 1 ? '/services' : index === 2 ? '/contact' : '/appointments';
                    navigate(route);
                  }}
                >
                  {slide.cta}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 transition-colors"
        data-testid="slider-prev-button"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6 text-white" />
      </button>
      <button
        onClick={goToNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 transition-colors"
        data-testid="slider-next-button"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6 text-white" />
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-colors ${
              index === currentSlide ? 'bg-white' : 'bg-white/40 hover:bg-white/60'
            }`}
            data-testid={`slider-dot-${index}`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroSlider;
